/* TODO: t() */
/* eslint-disable @typescript-eslint/no-floating-promises */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { Component, Fragment } from 'react';
import { View, StyleSheet } from 'react-native';

import { showError, showSuccess } from '../App/AppAlert';

import ensureObject from '../../support/shared/ensureObject';
import { postJSON } from '../../support/Fetch';
import AccountActions from '../../support/AccountActions';
import connectData, { ConnectedProps } from '../../support/connectData';
import createSubdomainUrl from '../../support/shared/createSubdomainUrl';
import { canUser } from '../../support/shared/permissionHelpers';

import {
  Card,
  Page,
  Button,
  ButtonToolbar,
  FormField,
  Link,
  Text,
  TextInputWithError,
} from '../uiold';
import { themeColors } from '../uiold/Constants';
import LoadingIndicator from '../uiold/LoadingIndicator';

import { Account } from '../../types/shared/Account';

const TITLE_MAX_LENGTH = 90;

type State = {
  subdomainTitle: string;
  isSaving: boolean;
};

class VendorPortalSettings extends Component<FullProps, State> {
  state: State = {
    subdomainTitle:
      (this.props.account && this.props.account.subdomainTitle) || '',
    isSaving: false,
  };

  render() {
    let subdomain = this.props.account?.subdomain ?? '';
    let { subdomainTitle } = this.state;
    let { user } = this.props;
    let canEdit = user && canUser(user, { marketPages: 'WRITE' });
    let vendorPortalUrl = createSubdomainUrl(location.host, subdomain);

    return (
      <Page>
        <Page.Header title="Vendor Portal Settings">
          {canEdit && this._renderSaveControls()}
        </Page.Header>
        <Card style={styles.marginBottom}>
          <FormField
            render={(id) => (
              <Fragment>
                <Text
                  style={styles.marginVertical}
                  accessibilityRole="label"
                  htmlFor={id}
                >
                  Subdomain
                </Text>
                <Text type="label" style={styles.description}>
                  This is the URL your visitors will see in their address bar.
                  The first part is custom to your market. If you need this
                  changed, please reach out to{' '}
                  <Link href="mailto:support@marketwurks.com">
                    support@marketwurks.com
                  </Link>
                  .
                </Text>
                <View style={styles.subdomainContainer}>
                  <Link
                    href={vendorPortalUrl}
                    target="_blank"
                    style={styles.subdomainLink}
                    hoverStyle={styles.subdomainLinkHovered}
                  >
                    {subdomain}.mymarket.org
                  </Link>
                </View>
              </Fragment>
            )}
          />
          <FormField
            render={(id) => (
              <View style={styles.titleContainer}>
                <Text
                  style={styles.marginVertical}
                  accessibilityRole="label"
                  htmlFor={id}
                >
                  Site Title
                </Text>
                <Text type="label" style={styles.description}>
                  This will be displayed by the browser and is visible to search
                  engines. Usually it should be the name of your market.
                </Text>
                <TextInputWithError
                  id={id}
                  disabled={!canEdit}
                  onChangeText={(text) =>
                    this._onValueChange('subdomainTitle', text)
                  }
                  value={subdomainTitle}
                  errorMessage={`Title must be no longer than ${TITLE_MAX_LENGTH} characters`}
                  checkError={this._checkSubdomainTitle}
                />
              </View>
            )}
          />
        </Card>
      </Page>
    );
  }

  _checkSubdomainTitle = (value: string) => {
    let title = value.trim();
    return title.length > 0 && title.length <= TITLE_MAX_LENGTH;
  };

  _renderSaveControls = () => {
    let { account } = this.props;
    let { isSaving, subdomainTitle } = this.state;
    let oldSubdomainTitle = (account && account.subdomainTitle) || '';
    if (subdomainTitle === oldSubdomainTitle) {
      return null;
    }
    return !isSaving ? (
      <LoadingIndicator />
    ) : (
      <ButtonToolbar>
        <Button title="Revert" onPress={this._onReset} />
        <Button title="Save" onPress={this._saveSettings} isPrimary />
      </ButtonToolbar>
    );
  };

  _onReset = () => {
    let { account } = this.props;
    this.setState({
      subdomainTitle: (account && account.subdomainTitle) || '',
      isSaving: true,
    });
  };

  _onValueChange = (fieldName: string, value: unknown) => {
    let { account } = this.props;
    if (!account) {
      return;
    }
    let oldValue: unknown = account[fieldName as keyof Account];
    if (value !== oldValue) {
      this.setState({ [fieldName]: value, isSaving: true } as any);
      return;
    } else {
      this.setState({ [fieldName]: value } as any);
    }
  };

  _saveSettings = () => {
    let { account } = this.props;
    let { subdomainTitle } = this.state;
    if (subdomainTitle.trim().length === 0) {
      showError('Title cannot be blank.');
      return;
    }
    if (!this._checkSubdomainTitle(subdomainTitle)) {
      showError(`Title must be no longer than ${TITLE_MAX_LENGTH} characters`);
      return;
    }

    if (account) {
      let accountID = account.id;
      let updates = {
        title: subdomainTitle,
      };
      postJSON(
        `/api/accounts/${accountID}/subdomain`,
        updates,
        (error, data) => {
          if (error) {
            let errorObject = ensureObject(error);
            let errorMessage = errorObject
              ? String(errorObject.message)
              : String(error);
            showError(`Unable to complete request: ${errorMessage}`);
          } else if (!data.success) {
            showError(data.error);
          } else if (data.success) {
            AccountActions.updateAccount({
              subdomainTitle,
            });
            this.setState({
              isSaving: false,
            });
            showSuccess('Settings successfully saved.');
          }
        },
      );
      this.setState({ isSaving: true });
    }
  };
}

let styles = StyleSheet.create({
  marginBottom: {
    marginBottom: 10,
  },
  marginVertical: {
    marginVertical: 5,
  },
  subdomainContainer: {
    flexDirection: 'row',
  },
  subdomainLink: {
    width: 350,
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderWidth: 1,
    borderColor: '#cacdd0',
    borderRadius: 2,
    lineHeight: 20,
    color: '#0a2841',
    fontSize: 14,
    backgroundColor: '#f1f1f1',
  },
  subdomainLinkHovered: {
    textDecorationLine: 'underline',
  },
  description: {
    color: themeColors.descGrey,
    marginBottom: 10,
  },
  titleContainer: {
    width: 350,
    paddingVertical: 5,
    marginTop: 30,
    marginBottom: 20,
  },
});

const connector = connectData()(() => ({
  account: { dataStoreKey: 'account' },
  user: { dataStoreKey: 'user' },
}));
type FullProps = ConnectedProps<typeof connector>;
export default connector(VendorPortalSettings);
